// The rail at the left of every place, drawn from the outside's data
// (`useShellMenu().menu.rail`, given by the host's `useRail`): Me (the mark)
// first, then the workspaces, then a way to make one, and at the foot the
// account (`menu.account`) with its menu. The place shown now has
// a bar at the rail's edge, and a workspace's tile turns squarer; while the
// making page is open, the way to make one has the bar instead. The cloud's
// web and the desktop app draw the same rail.
//
// An entry's `to` is a page of this screen, or a whole URL (`https://…`): the
// desktop app's own screen sends a workspace to the cloud's screen that way,
// and the app decides where it opens.
import { Link } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import type { ReactNode } from "react";
import { Avatar } from "../ui/bits";
import { BrandMark, Icon } from "../ui/icon";
import { MenuButton } from "../ui/menu";
import { Tooltip } from "../ui/tooltip";
import { AccountQuickSettings } from "./account-quick";
import type { ShellRailPlace } from "./outside/contract";
import { menuItemsOf, useShellMenu } from "./outside/source";

const ITEM =
  "relative flex h-9 w-9 flex-none cursor-pointer items-center justify-center rounded-[18px]";

// A workspace's tile: its first letter on one of six grounds. The server keeps
// no color, so the ground comes from the id: the same workspace has the same
// color on every device, and reordering never swaps colors.
const GROUNDS = [
  "bg-ws-1",
  "bg-ws-2",
  "bg-ws-3",
  "bg-ws-4",
  "bg-ws-5",
  "bg-ws-6",
] as const;

/** Which of the six grounds (1-6) a workspace has, from its id. */
export function workspaceGroundIndex(id: string): number {
  let h = 0;
  for (const ch of id) h = (h * 31 + (ch.codePointAt(0) ?? 0)) >>> 0;
  return (h % GROUNDS.length) + 1;
}

/** The ground's class for one of the six (1-6). */
export const groundClass = (ground: number): string =>
  GROUNDS[(ground - 1 + GROUNDS.length) % GROUNDS.length];

/** The first letter (an emoji counts as one). */
export function workspaceInitial(name: string): string {
  return ([...name.trim()][0] ?? "?").toUpperCase();
}

/** A tile as the outside's data gives it (`ShellPlaceMark` "tile"). */
export function TileMark({
  initial,
  ground,
  image,
  size = 36,
  current = false,
}: {
  initial: string;
  ground: number;
  /** The workspace's icon; its letter on the ground when there is none. */
  image?: string | null;
  size?: number;
  current?: boolean;
}) {
  const shape = {
    width: size,
    height: size,
    borderRadius: current ? Math.round(size * 0.3) : size / 2,
  };
  if (image)
    return (
      <img
        src={image}
        alt=""
        aria-hidden="true"
        draggable={false}
        width={size}
        height={size}
        className="flex-none object-cover transition-[border-radius] duration-150 ease-out"
        style={shape}
      />
    );
  return (
    <span
      aria-hidden="true"
      className={`inline-flex flex-none items-center justify-center text-ws-fg transition-[border-radius] duration-150 ease-out ${groundClass(ground)}`}
      style={{ ...shape, fontSize: Math.round(size * 0.38) }}
    >
      {initial}
    </span>
  );
}

/** The bar at the rail's edge beside the place shown now (grows, never pops in). */
function CurrentBar({
  on,
  bordered = false,
}: {
  on: boolean;
  /** Inside a 1px border (the way to make one): the rail's edge is 1px further. */
  bordered?: boolean;
}) {
  return (
    <span
      aria-hidden="true"
      className={`absolute top-1/2 ${bordered ? "-left-[9px]" : "-left-2"} w-[3px] -translate-y-1/2 rounded-r-sm bg-fg transition-[height] duration-150 ease-out`}
      style={{ height: on ? 20 : 0 }}
    />
  );
}

const isUrl = (to: string) => /^https?:\/\//.test(to);

/** A page of this screen, or a whole URL (the host decides where it opens). */
function RailTarget({
  to,
  label,
  current,
  className,
  onClick,
  children,
}: {
  to: string;
  label: string;
  current?: boolean;
  className: string;
  onClick?(): void;
  children: ReactNode;
}) {
  if (isUrl(to))
    return (
      <a href={to} aria-label={label} className={className} onClick={onClick}>
        {children}
      </a>
    );
  return (
    <Link
      to={to}
      aria-label={label}
      aria-current={current ? "page" : undefined}
      className={className}
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

/** A place on the rail. */
function RailLink({
  entry,
  onSelect,
}: {
  entry: ShellRailPlace;
  onSelect?(id: string): void;
}) {
  const current = entry.current === true;
  return (
    // Beside the item, not above: above the top one it lay over the mac
    // window's traffic lights (the person, 2026-10-18).
    <Tooltip text={entry.name} side="right">
      <RailTarget
        to={entry.to}
        label={entry.name}
        current={current}
        className={ITEM}
        onClick={onSelect && (() => onSelect(entry.id))}
      >
        <CurrentBar on={current} />
        {entry.mark.kind === "brand" ? (
          <BrandMark size={34} current={current} />
        ) : (
          <TileMark
            initial={entry.mark.initial}
            ground={entry.mark.ground}
            image={entry.mark.image}
            current={current}
          />
        )}
      </RailTarget>
    </Tooltip>
  );
}

/**
 * The rail the host's `useRail` describes (the extension `Rail`). `onSelect`
 * hears a place pressed (its id) before going there.
 */
export function PlaceRail({ onSelect }: { onSelect?(id: string): void } = {}) {
  const rail = useShellMenu().menu.rail;
  if (!rail) return null;
  const [first, ...rest] = rail.entries;
  return (
    <nav
      aria-label={rail.label}
      className="flex min-h-0 flex-1 flex-col items-center gap-[9px] pt-1"
    >
      {first?.type === "place" && (
        <RailLink entry={first} onSelect={onSelect} />
      )}
      {/* A line between Me and the workspaces (the person, 2026-10-30): a
          field's frame color at 60%, as the border color was not seen on the
          rail in either theme. The bar of the place shown sits at the rail's
          edge, outside the tiles, so the scrolling column keeps the full width
          and shows no scrollbar. */}
      <div
        aria-hidden="true"
        className="h-px w-6 flex-none bg-field-strong/60"
      />
      <div className="flex min-h-0 w-full flex-1 flex-col items-center gap-[9px] overflow-y-auto pb-2 [scrollbar-gutter:auto] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {rest.map((e) =>
          e.type === "place" ? (
            <RailLink key={e.id} entry={e} onSelect={onSelect} />
          ) : (
            <Tooltip key={e.id} text={e.label} side="right">
              <RailTarget
                to={e.to}
                label={e.label}
                current={e.current === true}
                className={`${ITEM} border border-dashed ${e.current ? "border-fg text-fg" : "border-field-strong text-muted"} hover:border-fg hover:text-fg`}
              >
                <CurrentBar on={e.current === true} bordered />
                <Icon kind="plus" size={15} />
              </RailTarget>
            </Tooltip>
          ),
        )}
      </div>
      <RailAccount />
    </nav>
  );
}

/**
 * The account at the rail's foot (the person, 2026-11-01): its picture, and
 * its menu (settings, shortcuts, sign out) with the theme and the language
 * under it, opening above it.
 */
function RailAccount() {
  const menu = useShellMenu();
  const account = menu.menu.account;
  if (!account) return null;
  const label = m.shell_account_menu();
  return (
    <>
      {/* The same line as the one under Me (the person, 2026-11-01). */}
      <div
        aria-hidden="true"
        className="h-px w-6 flex-none bg-field-strong/60"
      />
      <div className="flex flex-none justify-center pb-3">
        <Tooltip text={account.name} side="right">
          <MenuButton
            label={label}
            className={`${ITEM} hover:opacity-85`}
            items={menuItemsOf(account.menu, menu.select)}
            footer={<AccountQuickSettings />}
          >
            <Avatar
              name={account.name || "?"}
              image={account.image}
              size={32}
            />
          </MenuButton>
        </Tooltip>
      </div>
    </>
  );
}
